import { useEffect, useState } from 'react';
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/inline-state';

/** Lỗi tải chunk của route (import động hỏng): dev khi Vite tối ưu lại deps, production khi chunk cũ mất sau một lần deploy. */
const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const RELOAD_FLAG = 'xm.routeChunkReloadAt';
/** Chỉ tự tải lại một lần trong khoảng này, để chunk hỏng thật không tạo vòng tải lại vô hạn. */
const RELOAD_WINDOW_MS = 60_000;

export const isChunkLoadError = (error: unknown): boolean => CHUNK_ERROR.test(error instanceof Error ? error.message : String(error));

/** true đúng một lần mỗi RELOAD_WINDOW_MS; cờ lưu ở sessionStorage (đọc/ghi trong try/catch, không có storage thì không tự tải lại). */
export function claimChunkReload(now = Date.now()): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_FLAG));
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, String(now));
    return true;
  } catch {
    return false;
  }
}

/** errorComponent mặc định của router: thay "Something went wrong!" của TanStack. */
export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const chunk = isChunkLoadError(error);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    console.error(error);
    if (chunk && claimChunkReload()) {
      setReloading(true);
      window.location.reload();
    }
  }, [error, chunk]);

  if (reloading) return <Skeleton />;
  return (
    <div role="alert" className="flex flex-col items-start gap-2 rounded-xl border p-7">
      <h2 className="text-lg">Không mở được trang này</h2>
      <p className="text-sm text-muted-foreground">
        {chunk ? 'Ứng dụng vừa được cập nhật hoặc mạng chập chờn. Bấm Tải lại trang để lấy bản mới nhất.' : 'Có lỗi khi hiển thị trang. Bấm Thử lại, nếu vẫn không được hãy báo quản trị.'}
      </p>
      {/* Chunk hỏng thì invalidate không tải lại được file đã mất: phải tải lại cả trang để lấy index.html mới. */}
      <Button variant="outline" onClick={() => { if (chunk) window.location.reload(); else { reset(); void router.invalidate(); } }}>{chunk ? 'Tải lại trang' : 'Thử lại'}</Button>
    </div>
  );
}

/** Khung chờ mặc định khi route đang tải quá pendingMs (mặc định 1 giây). */
export function RoutePending() {
  return <Skeleton />;
}
